// ShareBite Report validation (Task 6.5).
//
// Pure validator for a Report submission. A submission is accepted — and a
// Report may be created — if and only if:
//   1. the selected reason is exactly one of the four allowed, case-sensitive
//      values ("Spam calls", "Fake organization", "Harassment",
//      "Misuse of the platform"), AND
//   2. the reporter's identity differs from the target's identity.
//
// Otherwise the submission is rejected and no Report is created. The function
// is platform-independent and side-effect free; the caller (a ReportService
// callable) performs any persistence using the validated fields.
//
// Design reference: design.md "Property 15: Report validation".
// Requirements:
//   17.2 - A filed Report requires exactly one reason from the set
//          {Spam calls, Fake organization, Harassment, Misuse of the platform}.
//   17.3 - A submission without a reason from the defined set is rejected and
//          no Report is created.
//   17.4 - A submission against the reporter's own account (self-report) is
//          rejected and no Report is created.
/**
 * The canonical set of exactly four allowed Report reasons (Req 17.2), in a
 * stable order. Values are exact and case-sensitive. Kept in lock-step with
 * the {@link ReportReason} union via the {@link isReportReason} guard.
 */
export const REPORT_REASONS = [
    "Spam calls",
    "Fake organization",
    "Harassment",
    "Misuse of the platform",
];
/**
 * Exact, case-sensitive membership test against the four allowed Report
 * reasons. Acts as a TypeScript type guard narrowing `string` to
 * {@link ReportReason}.
 *
 * @param value - Candidate string.
 * @returns `true` iff `value` exactly matches an allowed Report reason.
 */
export function isReportReason(value) {
    return REPORT_REASONS.includes(value);
}
/**
 * Validate a Report submission (Req 17.2, 17.3, 17.4).
 *
 * The submission is accepted — meaning a Report may be created — if and only
 * if `reason` exactly (case-sensitively) matches one of the allowed reasons
 * AND `reporterUid` differs from `targetUid`. In every other case the result
 * is a rejection carrying an explanatory error and no Report is created.
 *
 * Reason validity and the self-report check are independent constraints; when
 * both fail the reported `error` describes the invalid reason first.
 *
 * This function is pure: it reads only its arguments and returns a result
 * object without mutating any input or producing side effects.
 *
 * @param reporterUid - The filing user's identity (Google UID).
 * @param targetUid   - The reported user's identity (Google UID).
 * @param reason      - The selected reason string being submitted.
 * @returns A {@link ReportValidationResult} describing the outcome.
 */
export function validateReportSubmission(reporterUid, targetUid, reason) {
    if (!isReportReason(reason)) {
        return {
            accepted: false,
            error: `"${reason}" is not an allowed Report reason`,
        };
    }
    if (reporterUid === targetUid) {
        return {
            accepted: false,
            error: "A User may not file a Report against their own account",
        };
    }
    return { accepted: true, reason };
}
//# sourceMappingURL=reportValidation.js.map